import type { ColDef, ICellRendererParams, IRowNode } from "ag-grid-community";
import {
  createMultiSelectColumn,
  createSingleSelectColumn,
  createStringSelectColumn,
} from "../../utils/columnHelpers";
import {
  dateFormatter,
  dateTimeFormatter,
  LoanAmountCurrencyCellRenderer,
  numberFormatter,
  shortDateFormatter,
} from "../../utils/numberFormatter";
import type { Deal, Accordion } from "../../types/deal";
import React from "react";
import { useNavigate } from "react-router-dom";
import { DealNameLink } from "./components/DealNameLink";
import { NonCircumventBadge } from "../../components/deal/NonCircumventBadge";
import { AdvisorFeesMissingBadge } from "../../components/deal/AdvisorFeesMissingBadge";
import { dealHasNda } from "../../types/nda";
import { buildMeetingColumns } from "./pipelineMeetingColumns";
import { AMOUNT_FIELDS } from "./pipelineAmounts";
import { AmountCellEditor } from "./components/AmountCellEditor";
import {
  PriorityRenderer,
  ProbabilityBarRenderer,
} from "./components/OutlookRenderers";

// Also used by My Dashboard, which imports them from here.
export { PriorityRenderer, ProbabilityBarRenderer };

// Typed editors: without them every number/date/boolean cell was edited as raw text.
const NUMBER_FIELDS = new Set([
  "loan_amount_day_one",
  "loan_amount_fully_funded",
  "irr_local",
  "irr_usd",
  "irr_local_net",
  "irr_usd_net",
  "wal",
  "ltv",
  "ltc",
  "arrangement_fee",
  "margin",
  "rate_floor",
  "exit_fee",
  "initial_loan_term",
  "full_loan_term",
  "facility_agent_fee",
  "make_whole_fee_interestmonths",
  "commitment_fee",
  "extension_fee",
  "equity_contribution",
  "amortisation_percentage",
  "amortisation_amount",
  "covenant_ltv",
  "covenant_ltc",
  "covenant_icr",
  "covenant_dscr",
  "covenant_dy",
]);
const DATE_FIELDS = new Set(["target_closing", "proposed_ic_date"]);
const BOOLEAN_FIELDS = new Set([
  "is_portfolio",
  "is_development_loan",
  "has_complete_term_sheet",
  "has_complete_ten_pager",
  "is_funded",
  "amortisation",
]);

function withCellDataType(col: ColDef): ColDef {
  const field = col.field;
  if (!field) return col;
  // Money amounts are edited with thousands separators ("28,463,000").
  if (AMOUNT_FIELDS.has(field)) {
    return { cellDataType: "number", cellEditor: AmountCellEditor, ...col };
  }
  if (NUMBER_FIELDS.has(field)) return { cellDataType: "number", ...col };
  // API dates are ISO "YYYY-MM-DD" strings.
  if (DATE_FIELDS.has(field)) return { cellDataType: "dateString", ...col };
  if (BOOLEAN_FIELDS.has(field)) return { cellDataType: "boolean", ...col };
  return col;
}

/** Fills AG Grid cell vertical space (pipeline rows use stretch + flex) */
const pipelineCellShell: React.CSSProperties = {
  height: "100%",
  width: "100%",
  minHeight: 0,
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
};

// Custom renderer for deal links; addon rows link to the deal's Add-Ons tab.
// Deals with an NDA get the "NC" chip (opens the NDA tab); rows in closing
// without advisor fee quotes get a "Fees" chip (opens Advisor Fees). Both sit
// in the name cell so they show in every view, not only ones with Date Created.
const DealLinkRenderer = (params: ICellRendererParams<Deal | Accordion>) => {
  const navigate = useNavigate();
  const data = params.data as any;
  const name = params.value;

  const missingFees: string[] = Array.isArray(data?.advisor_fees_missing)
    ? data.advisor_fees_missing
    : [];
  const wrap = (
    node: React.ReactNode,
    dealId?: number | null,
    showNc = false,
  ) => (
    <div style={{ ...pipelineCellShell, gap: 4 }}>
      {node}
      {showNc && (
        <NonCircumventBadge
          compact
          nda={data?.nda}
          ndaId={data?.nda_id}
          onOpenNda={
            dealId != null
              ? () => navigate(`/deals/${dealId}?tab=nda`)
              : undefined
          }
        />
      )}
      {missingFees.length > 0 && dealId != null && (
        <AdvisorFeesMissingBadge
          missing={missingFees}
          onOpen={() => navigate(`/deals/${dealId}?tab=advisorFees`)}
        />
      )}
    </div>
  );

  if (!data) return wrap(name || "-");

  const parentDealId =
    typeof data.parent_deal_id === "number" ? data.parent_deal_id : null;
  const nestedDealId =
    typeof data.deal_id === "number" ? data.deal_id : null;
  const isAddonRow =
    parentDealId != null ||
    nestedDealId != null ||
    typeof data.accordion_id === "number";

  if (isAddonRow) {
    const dealId = parentDealId ?? nestedDealId;
    if (!dealId) return wrap(name || "-");
    return wrap(
      <DealNameLink
        dealId={dealId}
        name={name || `ID ${dealId}`}
        href={`/deals/${dealId}?tab=accordions`}
      />,
      dealId,
    );
  }

  const dealId =
    "id" in data && typeof data.id === "number" && data.id > 0
      ? data.id
      : undefined;

  if (!dealId) return wrap(name || "-");

  return wrap(
    <DealNameLink dealId={dealId} name={name || `ID ${dealId}`} />,
    dealId,
    dealHasNda(data),
  );
};

/** Options for `createSingleSelectColumn` — ids match API `Deal.probability` / `Deal.priority` */
export const PROBABILITY_OPTIONS = [
  { id: 25, name: "25%" },
  { id: 50, name: "50%" },
  { id: 75, name: "75%" },
  { id: 100, name: "100%" },
];
export const PRIORITY_OPTIONS = [
  { id: "High", name: "High" },
  { id: "Medium", name: "Medium" },
  { id: "Low", name: "Low" },
];

/** Ascending sort: High → Medium → Low; grid negates for desc (Low → Medium → High). Nulls last. */
const PRIORITY_SORT_RANK: Record<string, number> = {
  High: 1,
  Medium: 2,
  Low: 3,
};

function priorityToRank(p: unknown): number | null {
  if (p == null || typeof p !== "string") return null;
  const r = PRIORITY_SORT_RANK[p];
  return r !== undefined ? r : null;
}

export function dealPriorityComparator(
  _valueA: unknown,
  _valueB: unknown,
  nodeA: IRowNode<Deal>,
  nodeB: IRowNode<Deal>,
  isDescending: boolean,
): number {
  const ra = priorityToRank(nodeA?.data?.priority);
  const rb = priorityToRank(nodeB?.data?.priority);
  if (ra === null && rb === null) return 0;
  if (ra === null) return isDescending ? -1 : 1;
  if (rb === null) return isDescending ? 1 : -1;
  return ra - rb;
}

export function buildPipelineColumnDefs(args: {
  editMode: boolean;
  lists: any;
}): ColDef[] {
  const { editMode, lists } = args;
  const {
    condensedSponsors,
    sources,
    countries,
    sectors,
    status,
    users,
    loanTypes,
    riskClasses,
    securityTypes,
    transactionTypes,
    currencies,
    killReasons,
    capitalSources,
  } = lists ?? {};

  // Helper function to check if a row is an accordion
  const isAccordion = (params: any): boolean => {
    const data = params?.data;
    return data && "parent_deal_id" in data && data.parent_deal_id != null;
  };

  // Helper function to determine if a column should be editable
  // For accordions: only name, status, and loan amounts are editable
  // For regular deals: use the editMode flag
  const getEditable = (field: string, defaultEditable: boolean) => {
    if (!editMode) return false;
    return (params: any) => {
      if (isAccordion(params)) {
        // Accordions: only these fields are editable
        const accordionEditableFields = [
          "deal_name",
          "status",
          "loan_amount_day_one",
          "loan_amount_fully_funded",
          "margin",
          "ltc",
          "ltv",
          "irr_local",
          "irr_usd",
          "irr_local_net",
          "irr_usd_net",
          "wal",
        ];
        return accordionEditableFields.includes(field);
      }
      return defaultEditable;
    };
  };

  const columns: ColDef[] = [
    {
      field: "created_date",
      headerName: "Date Created",
      editable: false,
      width: 95,
      valueFormatter: shortDateFormatter,
      comparator: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      pinned: "left",
      filter: "agDateColumnFilter",
      filterValueGetter: (params) => {
        // Extract just the date part (set time to midnight) for proper date filtering
        if (!params.data?.created_date) return null;
        const date = new Date(params.data.created_date);
        if (isNaN(date.getTime())) return null;
        date.setHours(0, 0, 0, 0);
        return date;
      },
      filterParams: {
        filterOptions: [
          "inRange", // Between
          "greaterThan", // After
          "lessThan", // Before
        ],
        buttons: ["apply", "reset"],
        closeOnApply: true,
      },
    },
    {
      field: "deal_name",
      headerName: "Deal Name",
      editable: getEditable("deal_name", true),
      width: 220,
      cellRenderer: DealLinkRenderer,
      pinned: "left",
    },
    {
      ...createSingleSelectColumn(
        "status",
        "Status",
        status,
        "status",
        140,
        editMode,
      ),
      editable: getEditable("status", true),
    },
    {
      field: "deal_name_alias",
      headerName: "Deal Name Alias",
      editable: getEditable("deal_name_alias", true),
      width: 150,
    },
    { field: "city", headerName: "City", editable: false, width: 100 },
    {
      ...createSingleSelectColumn(
        "capital_source",
        "Capital Source",
        capitalSources,
        "name",
        80,
        editMode,
      ),
      editable: getEditable("capital_source", true),
    },
    {
      ...createMultiSelectColumn(
        "countries",
        "Countries",
        countries,
        "nicename",
        100,
        editMode,
      ),
      editable: getEditable("countries", true),
    },
    {
      ...createMultiSelectColumn(
        "sectors",
        "Sectors",
        sectors,
        "sector",
        100,
        editMode,
      ),
      editable: getEditable("sectors", true),
    },
    {
      ...createSingleSelectColumn(
        "sponsor",
        "Sponsor",
        condensedSponsors,
        "sponsor_name",
        150,
        editMode,
      ),
      editable: getEditable("sponsor", true),
    },
    {
      field: "loan_amount_day_one",
      headerName: "Loan Amount Day 1",
      editable: getEditable("loan_amount_day_one", true),
      width: 150,
      cellRenderer: LoanAmountCurrencyCellRenderer,
      valueFormatter: (params) => numberFormatter(params, true),
    },
    {
      field: "loan_amount_fully_funded",
      headerName: "Loan Amount FF",
      editable: getEditable("loan_amount_fully_funded", true),
      width: 120,
      cellRenderer: LoanAmountCurrencyCellRenderer,
      valueFormatter: (params) => numberFormatter(params, true),
    },
    {
      ...createSingleSelectColumn(
        "source_entity",
        "Source",
        sources,
        "source_name",
        90,
        editMode,
      ),
      editable: getEditable("source_entity", true),
    },
    {
      ...createSingleSelectColumn(
        "probability",
        "Probability (%)",
        PROBABILITY_OPTIONS,
        "name",
        100,
        editMode,
      ),
      editable: getEditable("probability", true),
      cellRenderer: ProbabilityBarRenderer,
      // Deal.probability is number | null — store scalar for API/save, not { id, label }
      valueSetter: (params: any) => {
        const nv = params.newValue;
        if (
          nv === null ||
          nv === undefined ||
          (typeof nv === "object" && nv !== null && nv.id === null)
        ) {
          if (params.data) params.data.probability = null;
          return true;
        }
        if (typeof nv === "object" && nv !== null && nv.id !== undefined) {
          if (params.data) params.data.probability = nv.id;
          return true;
        }
        return false;
      },
      comparator: (
        _valueA: unknown,
        _valueB: unknown,
        nodeA: IRowNode<Deal>,
        nodeB: IRowNode<Deal>,
        isDescending: boolean,
      ) => {
        const pa = nodeA?.data?.probability;
        const pb = nodeB?.data?.probability;
        const na = typeof pa === "number" && !Number.isNaN(pa) ? pa : null;
        const nb = typeof pb === "number" && !Number.isNaN(pb) ? pb : null;
        if (na === null && nb === null) return 0;
        if (na === null) return isDescending ? -1 : 1;
        if (nb === null) return isDescending ? 1 : -1;
        return na - nb;
      },
    },
    {
      ...createSingleSelectColumn(
        "priority",
        "Priority",
        PRIORITY_OPTIONS,
        "name",
        80,
        editMode,
      ),
      editable: getEditable("priority", true),
      cellRenderer: PriorityRenderer,
      valueSetter: (params: any) => {
        const nv = params.newValue;
        if (
          nv === null ||
          nv === undefined ||
          (typeof nv === "object" && nv !== null && nv.id === null)
        ) {
          if (params.data) params.data.priority = null;
          return true;
        }
        if (typeof nv === "object" && nv !== null && nv.id !== undefined) {
          if (params.data) params.data.priority = nv.id;
          return true;
        }
        return false;
      },
      comparator: dealPriorityComparator,
    },
    {
      ...createSingleSelectColumn(
        "kill_reason",
        "Kill Reason",
        killReasons,
        "kill_reason",
        120,
        editMode,
      ),
      editable: getEditable("kill_reason", true),
    },
    {
      ...createSingleSelectColumn(
        "deal_lead",
        "Deal Lead",
        users,
        "name",
        120,
        editMode,
      ),
      editable: getEditable("deal_lead", true),
    },
    {
      ...createSingleSelectColumn(
        "deal_support",
        "Deal Support",
        users,
        "name",
        120,
        editMode,
      ),
      editable: getEditable("deal_support", true),
    },
    {
      ...createStringSelectColumn(
        "loan_amount_currency",
        "Currency",
        currencies,
        100,
        editMode,
      ),
      editable: getEditable("loan_amount_currency", true),
    },
    {
      field: "target_closing",
      headerName: "Expected Closing Date",
      editable: getEditable("target_closing", true),
      width: 150,
      valueFormatter: dateFormatter,
    },
    {
      field: "proposed_ic_date",
      headerName: "IC Date",
      editable: getEditable("proposed_ic_date", true),
      width: 150,
      valueFormatter: dateFormatter,
    },
    {
      field: "irr_local",
      headerName: "IRR Local (%)",
      editable: getEditable("irr_local", true),
      width: 100,
    },
    {
      field: "irr_usd",
      headerName: "IRR USD (%)",
      editable: getEditable("irr_usd", true),
      width: 100,
    },
    {
      field: "irr_local_net",
      headerName: "IRR Local (Excl. TFC fees)",
      editable: getEditable("irr_local_net", true),
      width: 160,
    },
    {
      field: "irr_usd_net",
      headerName: "IRR USD (Excl. TFC fees)",
      editable: getEditable("irr_usd_net", true),
      width: 150,
    },
    {
      field: "wal",
      headerName: "WAL",
      editable: getEditable("wal", true),
      width: 100,
    },
    {
      ...createSingleSelectColumn(
        "secondary_sponsor",
        "Secondary Sponsor / Manager",
        condensedSponsors,
        "sponsor_name",
        150,
        editMode,
      ),
      editable: getEditable("secondary_sponsor", true),
    },
    {
      ...createSingleSelectColumn(
        "loan_type",
        "Loan Type",
        loanTypes,
        "loan_type",
        150,
        editMode,
      ),
      editable: getEditable("loan_type", true),
    },
    {
      field: "cm_rating",
      headerName: "CM Rating",
      editable: getEditable("cm_rating", true),
      width: 100,
    },
    {
      field: "ltv",
      headerName: "LTV (%)",
      editable: getEditable("ltv", true),
      width: 80,
    },
    {
      field: "ltc",
      headerName: "LTC (%)",
      editable: getEditable("ltc", true),
      width: 80,
    },
    {
      field: "arrangement_fee",
      headerName: "Arrangement Fee (%)",
      editable: getEditable("arrangement_fee", true),
      width: 100,
    },
    {
      field: "margin",
      headerName: "Margin (%)",
      editable: getEditable("margin", true),
      width: 100,
    },
    {
      field: "rate_floor",
      headerName: "Rate Floor (%)",
      editable: getEditable("rate_floor", true),
      width: 100,
    },
    {
      field: "exit_fee",
      headerName: "Exit Fee (%)",
      editable: getEditable("exit_fee", true),
      width: 100,
    },
    {
      field: "initial_loan_term",
      headerName: "Initial Loan Term (y)",
      editable: getEditable("initial_loan_term", true),
      width: 100,
    },
    {
      field: "full_loan_term",
      headerName: "Full Loan Term (y)",
      editable: getEditable("full_loan_term", true),
      width: 100,
    },
    {
      field: "facility_agent_fee",
      headerName: "Facility Agent Fee (p.a)",
      editable: getEditable("facility_agent_fee", true),
      width: 100,
      valueFormatter: numberFormatter,
    },
    {
      field: "make_whole_fee_interestmonths",
      headerName: "Make Whole Fee (months of interest)",
      editable: getEditable("make_whole_fee_interestmonths", true),
      width: 135,
    },
    {
      field: "commitment_fee",
      headerName: "Commitment Fee (%)",
      editable: getEditable("commitment_fee", true),
      width: 100,
    },
    {
      field: "extension_fee",
      headerName: "Extension Fee (%)",
      editable: getEditable("extension_fee", true),
      width: 100,
    },

    {
      field: "equity_contribution",
      headerName: "Equity Contribution",
      editable: getEditable("equity_contribution", true),
      width: 100,
    },
    {
      field: "amortisation",
      headerName: "Amortisation",
      editable: getEditable("amortisation", true),
      width: 100,
    },
    {
      field: "amortisation_percentage",
      headerName: "Amortisation (%)",
      editable: getEditable("amortisation_percentage", true),
      width: 100,
    },
    {
      field: "amortisation_amount",
      headerName: "Amortisation (p.a)",
      editable: getEditable("amortisation_amount", true),
      width: 100,
    },
    {
      field: "covenant_ltv",
      headerName: "Covenant LTV (%)",
      editable: getEditable("covenant_ltv", true),
      width: 90,
    },
    {
      field: "covenant_ltc",
      headerName: "Covenant LTC (%)",
      editable: getEditable("covenant_ltc", true),
      width: 90,
    },
    {
      field: "covenant_icr",
      headerName: "Covenant ICR (%)",
      editable: getEditable("covenant_icr", true),
      width: 90,
    },
    {
      field: "covenant_dscr",
      headerName: "Covenant DSCR (%)",
      editable: getEditable("covenant_dscr", true),
      width: 90,
    },
    {
      field: "covenant_dy",
      headerName: "Covenant DY (%)",
      editable: getEditable("covenant_dy", true),
      width: 90,
    },
    {
      ...createSingleSelectColumn(
        "security_type",
        "Security Type",
        securityTypes,
        "security_type",
        100,
        editMode,
      ),
      editable: getEditable("security_type", true),
    },
    {
      ...createSingleSelectColumn(
        "transaction_type",
        "Transaction Type",
        transactionTypes,
        "transaction_type",
        100,
        editMode,
      ),
      editable: getEditable("transaction_type", true),
    },
    {
      ...createSingleSelectColumn(
        "risk_class",
        "Risk Class",
        riskClasses,
        "risk_class",
        100,
        editMode,
      ),
      editable: getEditable("risk_class", true),
    },
    {
      field: "is_portfolio",
      headerName: "Portfolio",
      editable: getEditable("is_portfolio", true),
      width: 100,
    },
    {
      field: "is_development_loan",
      headerName: "Development",
      editable: getEditable("is_development_loan", true),
      width: 100,
    },
    {
      field: "has_complete_term_sheet",
      headerName: "Term Sheet",
      editable: getEditable("has_complete_term_sheet", true),
      width: 100,
    },
    {
      field: "has_complete_ten_pager",
      headerName: "Ten Pager",
      editable: getEditable("has_complete_ten_pager", true),
      width: 100,
    },
    {
      field: "is_funded",
      headerName: "Funded",
      editable: getEditable("is_funded", true),
      width: 100,
    },
    {
      field: "loan_amount_day_one_gbp",
      headerName: "Loan Amount Day 1 GBP",
      editable: false,
      width: 150,
      valueFormatter: numberFormatter,
    },
    {
      field: "loan_amount_fully_funded_gbp",
      headerName: "Loan Amount Fully Funded GBP",
      editable: false,
      width: 150,
      valueFormatter: numberFormatter,
    },
    {
      field: "updated_date",
      headerName: "Last Updated",
      // Set by the API on every save; never user-editable.
      editable: false,
      width: 200,
      valueFormatter: dateTimeFormatter,
      comparator: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    },
  ];

  return [
    ...columns.map(withCellDataType),
    ...buildMeetingColumns(editMode),
  ];
}
